<script setup lang="ts">
import { inject, onBeforeMount, provide, ref, watch } from 'vue'

import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import GoogleMap from '@/components/GoogleMap'
import LeftMenu from '@/components/LeftMenu.vue'

import assortments from '@/assortments'
import config from '@/config'
import language from '@/lang/index.json'
import filterStoresByAssortment from '@/lib/filterStoresByAssortment'
import useCurrentUserLocation from '@/lib/useCurrentUserLocation'
import useCenter from '@/lib/useMapCenter'
import { useMobile } from '@/lib/useMedia'
import type { Event, Language, Place, Store, Stores } from '@/types'

const type = 'store'
provide('type', type)

const BRAND = inject<string>('BRAND') || 'satch'

const router = useRoute()
const { locale } = useI18n()
const isMobile = useMobile()
const lang = (router.params.lang as Language) || 'de'
locale.value = lang

const map = ref<google.maps.Map>()
const availableFilters = assortments[BRAND]

const { center, updateCenter } = useCenter()

const stores = ref<Stores>()
const filteredStores = ref<{ type: string; features: Store[] }>({
  type: 'FeatureCollection',
  features: [],
})

const loaded = ref<boolean>(false)
const showEventsList = ref<boolean>(false)
const selectedEvent = ref<(Store & Event) | null>(null)

const settings = config.settings[lang][isMobile.value ? 'mobile' : 'desktop']

const shouldShow = (show: boolean) => (showEventsList.value = show)

const navigateTo = (place: Place): void => {
  showEventsList.value = true
  selectedEvent.value = place as (Store & Event) | null
  updateCenter(place.geometry?.coordinates)
  map.value?.panTo(place.geometry?.coordinates)
  if (map.value?.getZoom() != config.settings.panToZoom) {
    map.value?.setZoom(config.settings.panToZoom)
  }
}

const updateFilters = (filters: string[]) => {
  if (!stores.value) {
    return
  }

  // the value as to be assigned like this, since doing `filteredStores.value.features`
  // seems to be non-reactive and after executing the `filterStoresByAssortment` once,
  // the filteredStores will be empty after the second execution

  filteredStores.value = {
    type: 'FeatureCollection',
    features: stores.value.features.filter((store) =>
      filterStoresByAssortment({
        assortments: store.properties.assortment,
        filters,
        stores: stores.value?.features,
      }),
    ),
  }

  document.dispatchEvent(
    new CustomEvent('assortmentChanged', {
      detail: filters,
    }),
  )
}

onBeforeMount(async () => {
  try {
    const response = await (
      await fetch(`${import.meta.env.PUBLIC_GEO_JSON_STORES}`)
    ).json()

    const searchParams = new URLSearchParams(window.location.search)

    const filters = searchParams.get('filter')?.split(',') || []

    stores.value = response

    if (filters?.length) {
      filteredStores.value = {
        type: 'FeatureCollection',
        features: response.features.filter((store: Store) =>
          filterStoresByAssortment({
            assortments: store.properties.assortment,
            filters,
            stores: response.features,
          }),
        ),
      }
    } else if (!filters?.length) {
      filteredStores.value = response
    }

    map.value = await GoogleMap({
      response,
      lang,
      navigateTo,
    })

    loaded.value = true
  } catch (error) {
    console.log(error)
    map.value = await GoogleMap({
      response: { features: [] },
      lang,
      navigateTo,
    })
  }
})

watch(map, () => {
  useCurrentUserLocation(map.value, language[lang].locationDisabled, lang)
})
</script>
<template>
  <div id="map" />
  <LeftMenu
    v-if="loaded && filteredStores"
    :map-ref="map"
    :places="filteredStores"
    :available-filters="availableFilters"
    :selected-place="selectedEvent"
    :center="center"
    :default-zoom="settings.defaultZoom"
    :show-store-list="showEventsList"
    @navigate-to="navigateTo"
    @remove-store="selectedEvent = null"
    @should-show="shouldShow"
    @update-filters="updateFilters"
  />
</template>
<style>
#map {
  height: 100%;
}

#app {
  height: 100%;
}

html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
}
</style>
